import { Injectable } from '@angular/core';
import { Project } from '../../models';
import { FormElement, ObjectProperties, Properties, Schema, Section } from '../logic-services/models';

@Injectable({
    providedIn: 'root'
})
export class SkhemaUtilityService {

    private _virtualUiSchemaCache = new Map<string, { value: FormElement, documentVersion: number; }>();
    private _virtualSectionsCache = new Map<string, { value: Section[], documentVersion: number; }>();
    private _virtualSchemaCache = new Map<string, { value: Schema, documentVersion: number; }>();

    constructor() { }

    virtualSections(project: Project): Section[] {
        if (!project?.id) {
            return [];
        }

        const cacheKey = project.id;
        const documentVersion = project.documentVersion;
        const cached = this._virtualSectionsCache.get(cacheKey);

        if (cached) {
            if (cached.documentVersion === documentVersion) {
                return cached.value;
            }
            else {
                this._virtualSectionsCache.delete(cacheKey);
            }
        }

        const virtualSections = new Array<Section>();

        project.JSONForms?.Sections?.forEach((jsonFormSection: Section) => {
            virtualSections.push(jsonFormSection);
        });

        this._virtualSectionsCache.set(cacheKey, { value: virtualSections, documentVersion: documentVersion });

        return virtualSections;
    }

    virtualUiSchema(virtualSections: Section[], project: Project): FormElement {
        if (!project?.id) {
            return new FormElement();
        }

        const cacheKey = project.id;
        const documentVersion = project.documentVersion;
        const cached = this._virtualUiSchemaCache.get(cacheKey);

        if (cached) {
            if (cached.documentVersion === documentVersion) {
                return cached.value;
            }
            else {
                this._virtualUiSchemaCache.delete(cacheKey);
            }
        }

        const jsonSchemaMap = this.createSchemaElementMap(project.JSONForms?.UISchema?.elements || []);

        const virtualUiSchema = new FormElement();
        virtualUiSchema.type = ObjectProperties.VerticalLayout;
        virtualUiSchema.elements = [];

        for (const section of virtualSections) {
            if (!section) {
                continue;
            }

            const schema = jsonSchemaMap.get(section.id);

            if (schema) {
                virtualUiSchema.elements.push(schema);
            }
        }

        this._virtualUiSchemaCache.set(cacheKey, { value: virtualUiSchema, documentVersion: documentVersion });

        return virtualUiSchema;
    }

    virtualSchema(project: Project): Schema {
        if (!project?.id) {
            return new Schema();
        }

        const cacheKey = project.id;
        const documentVersion = project.documentVersion;
        const cached = this._virtualSchemaCache.get(cacheKey);

        if (cached) {
            if (cached.documentVersion === documentVersion) {
                return cached.value;
            }
            else {
                this._virtualSchemaCache.delete(cacheKey);
            }
        }

        const virtualSchema = new Schema();
        virtualSchema.properties = new Properties();
        virtualSchema.type = ObjectProperties.Object;

        for (const key in project.JSONForms?.Schema?.properties) {
            virtualSchema.properties[key] = project.JSONForms.Schema.properties[key];
        }

        this._virtualSchemaCache.set(cacheKey, { value: virtualSchema, documentVersion: documentVersion });

        return virtualSchema;
    }

    clearCache(projectId: string): void {
        this._virtualSectionsCache.delete(projectId);
        this._virtualUiSchemaCache.delete(projectId);
        this._virtualSchemaCache.delete(projectId);
    }

    private createSchemaElementMap(elements: FormElement[]): Map<string, FormElement> {
        const map = new Map<string, FormElement>();
        for (const element of elements) {
            if (element.id) {
                map.set(element.id, element);
            }
        }
        return map;
    }
}
